/**
 * Account lead history — the client's copy of the period rules, the wording,
 * the error-code messages and the spreadsheet parser for the bulk import
 * (owner, 2026-10-25).
 *
 * A company's account lead is an effective-dated list of periods
 * `(user, startsOn, endsOn | null)`. Gaps are allowed, overlaps never, and at
 * most one period is open (`endsOn: null`). No period may start or end after
 * today. The SERVER is the decision (`account-lead-history.rules.ts`); these
 * checks only let a form refuse early with the same codes, so the message a
 * person sees is the same whichever side caught it.
 *
 * "Today" is the UTC calendar day, because the server's in-future check and its
 * "current lead" invariant both run on `UTC_DATE()`. A viewer east of Greenwich
 * after local midnight would otherwise be offered a day the server calls
 * tomorrow. That is why this module uses `todayUtcIsoDate`, not the
 * viewer-local `todayIsoDate` from invoice-utils.
 *
 * Every business date here is a bare 'YYYY-MM-DD' string compared as text.
 * The only `Date` use is turning a spreadsheet's date cell into that string.
 */
import { formatDateOnly } from '@/lib/format';
import { todayUtcIsoDate } from '@/lib/stage-status-follow-up';
import type { AccountLeadImportRowInput } from '@/lib/types';

// ---------------------------------------------------------------- wording

export const LEAD_HISTORY_TITLE = 'Account lead history';
export const EFFECTIVE_FROM_LABEL = 'Effective from';
export const CREDITED_TO_LABEL = 'Credited to';
/** Shown in the "To" column for the open period. */
export const CURRENT_PERIOD_LABEL = 'Current';

/** Today's UTC calendar day as 'YYYY-MM-DD' (the server's "today"). */
export const todayIsoUtc = todayUtcIsoDate;

// ---------------------------------------------------------------- error codes

export type AccountLeadErrorCode =
  | 'ACCOUNT_LEAD_PERIOD_OVERLAP'
  | 'ACCOUNT_LEAD_PERIOD_ENDS_BEFORE_START'
  | 'ACCOUNT_LEAD_PERIOD_IN_FUTURE'
  | 'ACCOUNT_LEAD_PERIOD_MULTIPLE_OPEN'
  | 'ACCOUNT_LEAD_EFFECTIVE_FROM_IN_FUTURE'
  | 'ACCOUNT_LEAD_EFFECTIVE_FROM_BEFORE_CURRENT_PERIOD'
  | 'ACCOUNT_LEAD_EFFECTIVE_FROM_WITHOUT_LEAD'
  | 'IMPORT_COMPANY_NOT_FOUND'
  | 'IMPORT_COMPANY_AMBIGUOUS'
  | 'IMPORT_LEAD_NOT_FOUND'
  | 'IMPORT_LEAD_AMBIGUOUS'
  | 'IMPORT_LEAD_NOT_ACCOUNT_LEAD';

export const ACCOUNT_LEAD_ERROR_MESSAGES: Readonly<Record<AccountLeadErrorCode, string>> = {
  ACCOUNT_LEAD_PERIOD_OVERLAP: 'This period overlaps another account lead period.',
  ACCOUNT_LEAD_PERIOD_ENDS_BEFORE_START: 'The end date is before the start date.',
  ACCOUNT_LEAD_PERIOD_IN_FUTURE: 'A period cannot start or end after today.',
  ACCOUNT_LEAD_PERIOD_MULTIPLE_OPEN: 'Only one period can be current.',
  ACCOUNT_LEAD_EFFECTIVE_FROM_IN_FUTURE: 'The effective date cannot be after today.',
  ACCOUNT_LEAD_EFFECTIVE_FROM_BEFORE_CURRENT_PERIOD:
    'The effective date is before the current period started. Change it in the account lead history.',
  ACCOUNT_LEAD_EFFECTIVE_FROM_WITHOUT_LEAD: 'An effective date needs an account lead change.',
  IMPORT_COMPANY_NOT_FOUND: 'No company matches this name or id.',
  IMPORT_COMPANY_AMBIGUOUS: 'More than one company matches this name. Use the company id.',
  IMPORT_LEAD_NOT_FOUND: 'No user matches this account lead.',
  IMPORT_LEAD_AMBIGUOUS: 'More than one user matches this name. Use their email address.',
  IMPORT_LEAD_NOT_ACCOUNT_LEAD: 'This user is not in the Account lead group.',
};

/** The message for a known code, else null (fall back to the server's message). */
export function accountLeadErrorMessage(code: string | null | undefined): string | null {
  if (!code) return null;
  return Object.prototype.hasOwnProperty.call(ACCOUNT_LEAD_ERROR_MESSAGES, code)
    ? ACCOUNT_LEAD_ERROR_MESSAGES[code as AccountLeadErrorCode]
    : null;
}

// ---------------------------------------------------------------- dates

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** True for a real calendar date in 'YYYY-MM-DD' (2026-02-30 is false). */
export function isIsoDate(value: string): boolean {
  const match = ISO_DATE.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1) return false;
  return day <= daysInMonth(year, month);
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return leap ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

function isoFromUtcParts(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const MS_PER_DAY = 86_400_000;

/**
 * An Excel serial day number as 'YYYY-MM-DD', or null.
 *
 * 1900 system: day 0 is 1899-12-30 (that epoch absorbs Excel's fictitious
 * 1900-02-29 for every serial from 61 on, which is every date anyone will type
 * here). 1904 system: day 0 is 1904-01-01. Pure UTC arithmetic; a fractional
 * time-of-day part is dropped.
 */
export function excelSerialToIsoDate(serial: number, date1904 = false): string | null {
  if (!Number.isFinite(serial) || serial < 1) return null;
  const epoch = date1904 ? Date.UTC(1904, 0, 1) : Date.UTC(1899, 11, 30);
  const at = new Date(epoch + Math.floor(serial) * MS_PER_DAY);
  return isoFromUtcParts(at.getUTCFullYear(), at.getUTCMonth() + 1, at.getUTCDate());
}

/**
 * A spreadsheet `Date` cell as 'YYYY-MM-DD'.
 *
 * Spreadsheet libraries build date cells at local midnight in some versions and
 * at UTC midnight in others. Rounding to the NEAREST UTC midnight gives the
 * intended calendar day either way for any offset under 12 hours.
 */
function dateCellToIsoDate(value: Date): string | null {
  const ms = value.getTime();
  if (!Number.isFinite(ms)) return null;
  const at = new Date(Math.round(ms / MS_PER_DAY) * MS_PER_DAY);
  return isoFromUtcParts(at.getUTCFullYear(), at.getUTCMonth() + 1, at.getUTCDate());
}

/**
 * One date cell → 'YYYY-MM-DD'. Blank → `''`; unreadable → null.
 * Accepts 'YYYY-MM-DD' text, an Excel serial (number or all-digit text) and a
 * `Date` object.
 */
function cellToIsoDate(value: unknown, date1904: boolean): string | null | '' {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return dateCellToIsoDate(value);
  if (typeof value === 'number') return excelSerialToIsoDate(value, date1904);
  const text = String(value).trim();
  if (text === '') return '';
  if (isIsoDate(text)) return text;
  if (/^\d{1,6}(\.\d+)?$/.test(text)) return excelSerialToIsoDate(Number(text), date1904);
  return null;
}

// ---------------------------------------------------------------- period rules

/** The fields the rules read. `AccountLeadPeriod` satisfies it. */
export interface LeadPeriodLike {
  startsOn: string;
  endsOn: string | null;
}

export interface LeadPeriodError {
  code: Extract<
    AccountLeadErrorCode,
    | 'ACCOUNT_LEAD_PERIOD_OVERLAP'
    | 'ACCOUNT_LEAD_PERIOD_ENDS_BEFORE_START'
    | 'ACCOUNT_LEAD_PERIOD_IN_FUTURE'
    | 'ACCOUNT_LEAD_PERIOD_MULTIPLE_OPEN'
  >;
  message: string;
  /** Indexes into the array passed in; two for an overlap (the clashing pair). */
  indexes: number[];
}

function periodError(code: LeadPeriodError['code'], indexes: number[]): LeadPeriodError {
  return { code, message: ACCOUNT_LEAD_ERROR_MESSAGES[code], indexes };
}

/**
 * Every rule a set of periods breaks, mirroring the server's `validatePeriods`:
 * end before start, start or end after `todayIso`, more than one open period,
 * and overlaps (inclusive dates: a period ending 2026-05-31 and one starting
 * 2026-05-31 overlap; starting 2026-06-01 is the clean hand-over). An empty
 * array means the set is valid.
 */
export function validateLeadPeriods(
  periods: readonly LeadPeriodLike[],
  todayIso: string = todayIsoUtc(),
): LeadPeriodError[] {
  const errors: LeadPeriodError[] = [];
  periods.forEach((period, index) => {
    if (period.endsOn !== null && period.endsOn < period.startsOn) {
      errors.push(periodError('ACCOUNT_LEAD_PERIOD_ENDS_BEFORE_START', [index]));
    }
    if (period.startsOn > todayIso || (period.endsOn !== null && period.endsOn > todayIso)) {
      errors.push(periodError('ACCOUNT_LEAD_PERIOD_IN_FUTURE', [index]));
    }
  });

  const open = periods.flatMap((period, index) => (period.endsOn === null ? [index] : []));
  if (open.length > 1) errors.push(periodError('ACCOUNT_LEAD_PERIOD_MULTIPLE_OPEN', open));

  const order = periods
    .map((_, index) => index)
    .sort((a, b) => compareText(periods[a]!.startsOn, periods[b]!.startsOn));
  for (let i = 1; i < order.length; i += 1) {
    const earlierIndex = order[i - 1]!;
    const laterIndex = order[i]!;
    const earlier = periods[earlierIndex]!;
    const later = periods[laterIndex]!;
    if (earlier.endsOn === null || earlier.endsOn >= later.startsOn) {
      errors.push(periodError('ACCOUNT_LEAD_PERIOD_OVERLAP', [earlierIndex, laterIndex]));
    }
  }
  return errors;
}

function compareText(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** True when the period covers `todayIso` (the server's `isCurrent`). */
export function isPeriodCurrent(period: LeadPeriodLike, todayIso: string = todayIsoUtc()): boolean {
  return period.startsOn <= todayIso && (period.endsOn === null || period.endsOn >= todayIso);
}

/** Newest first by start date (the order the server lists them). */
export function sortPeriodsNewestFirst<T extends LeadPeriodLike>(periods: readonly T[]): T[] {
  return [...periods].sort((a, b) => compareText(b.startsOn, a.startsOn));
}

/** "5 Jan 2026 – 31 May 2026", or "5 Jan 2026 – Current" for the open period. */
export function periodLabel(period: LeadPeriodLike): string {
  const to = period.endsOn === null ? CURRENT_PERIOD_LABEL : formatDateOnly(period.endsOn);
  return `${formatDateOnly(period.startsOn)} – ${to}`;
}

// ---------------------------------------------------------------- import sheet

export const LEAD_HISTORY_SHEET_HEADERS = ['Company', 'Account lead', 'From', 'To'] as const;

/** Header row only; downloaded as the import template. */
export const LEAD_HISTORY_TEMPLATE_CSV = `${LEAD_HISTORY_SHEET_HEADERS.join(',')}\r\n`;

/** The server's per-request cap (`importSchema.rows`, 1..5000). */
export const LEAD_HISTORY_IMPORT_MAX_ROWS = 5000;

export interface ParsedLeadHistorySheet {
  rows: AccountLeadImportRowInput[];
  /**
   * The spreadsheet row number (header = row 1) of each entry in `rows`, so a
   * preview can point at the sheet. `rows[i]` is import row `i + 1`.
   */
  sheetRowNumbers: number[];
  /**
   * Problems found while reading. Rows with a problem are left out of `rows`;
   * because each company's rows REPLACE its whole history, a caller must not
   * send `rows` while this is non-empty.
   */
  errors: string[];
}

/**
 * Split CSV text into rows of cells (RFC 4180): commas, double-quoted fields
 * that may contain commas, newlines and `""` escapes, CRLF or LF line ends, and
 * a leading byte-order mark.
 */
export function parseCsv(text: string): string[][] {
  const source = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  let i = 0;
  while (i < source.length) {
    const ch = source[i]!;
    if (quoted) {
      if (ch === '"') {
        if (source[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        quoted = false;
        i += 1;
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }
    if (ch === '"' && field === '') {
      quoted = true;
    } else if (ch === ',') {
      row.push(field);
      field = '';
    } else if (ch === '\r' || ch === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      if (ch === '\r' && source[i + 1] === '\n') i += 1;
    } else {
      field += ch;
    }
    i += 1;
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

function normaliseHeader(value: unknown): string {
  return String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function cellText(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim().replace(/\s+/g, ' ');
}

function isBlankRow(row: readonly unknown[]): boolean {
  return row.every((cell) => cellText(cell) === '');
}

/** Raw cell grid of the first sheet, plus whether the workbook uses 1904 dates. */
async function readGrid(file: File): Promise<{ grid: unknown[][]; date1904: boolean }> {
  const isCsv = /\.(csv|txt)$/i.test(file.name) || file.type === 'text/csv';
  if (isCsv) return { grid: parseCsv(await file.text()), date1904: false };

  const XLSX = await import('xlsx');
  // No `cellDates`: date cells stay Excel serial numbers and are converted with
  // UTC arithmetic above, never through a timezone.
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const date1904 = Boolean(workbook.Workbook?.WBProps?.date1904);
  const firstName = workbook.SheetNames[0];
  const sheet = firstName ? workbook.Sheets[firstName] : undefined;
  if (!sheet) return { grid: [], date1904 };
  const grid = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: true,
    defval: '',
    blankrows: true,
  }) as unknown[][];
  return { grid, date1904 };
}

/**
 * Read an account lead history sheet (.xlsx, .xls or .csv) with the columns
 * Company, Account lead, From, To (headers matched case- and
 * space-insensitively, in any order; other columns are ignored). From is
 * required; a blank To means the current period. Dates are 'YYYY-MM-DD' text
 * or real spreadsheet dates. Wholly blank rows are skipped.
 */
export async function parseLeadHistorySheet(file: File): Promise<ParsedLeadHistorySheet> {
  let grid: unknown[][];
  let date1904: boolean;
  try {
    ({ grid, date1904 } = await readGrid(file));
  } catch {
    return { rows: [], sheetRowNumbers: [], errors: ['This file could not be read.'] };
  }

  const headerIndex = grid.findIndex((row) => !isBlankRow(row));
  if (headerIndex < 0) return { rows: [], sheetRowNumbers: [], errors: ['The file is empty.'] };

  const header = grid[headerIndex]!.map(normaliseHeader);
  const columns = LEAD_HISTORY_SHEET_HEADERS.map((name) => header.indexOf(name.toLowerCase()));
  const missing = LEAD_HISTORY_SHEET_HEADERS.filter((_, i) => columns[i]! < 0);
  if (missing.length > 0) {
    return {
      rows: [],
      sheetRowNumbers: [],
      errors: [`Missing column${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}.`],
    };
  }
  const [companyCol, leadCol, fromCol, toCol] = columns as [number, number, number, number];

  const rows: AccountLeadImportRowInput[] = [];
  const sheetRowNumbers: number[] = [];
  const errors: string[] = [];

  for (let r = headerIndex + 1; r < grid.length; r += 1) {
    const row = grid[r]!;
    if (isBlankRow(row)) continue;
    const sheetRow = r + 1;
    const rowErrors: string[] = [];

    const company = cellText(row[companyCol]);
    const lead = cellText(row[leadCol]);
    const startsOn = cellToIsoDate(row[fromCol], date1904);
    const endsOn = cellToIsoDate(row[toCol], date1904);

    if (company === '') rowErrors.push('Company is blank');
    if (lead === '') rowErrors.push('Account lead is blank');
    if (startsOn === '') rowErrors.push('From is blank');
    else if (startsOn === null) rowErrors.push('From is not a date (use YYYY-MM-DD)');
    if (endsOn === null) rowErrors.push('To is not a date (use YYYY-MM-DD)');

    if (rowErrors.length > 0 || startsOn === null || startsOn === '' || endsOn === null) {
      errors.push(`Row ${sheetRow}: ${rowErrors.join('; ')}.`);
      continue;
    }
    rows.push({ company, lead, startsOn, endsOn: endsOn === '' ? null : endsOn });
    sheetRowNumbers.push(sheetRow);
  }

  if (rows.length === 0 && errors.length === 0) errors.push('The file has no rows.');
  if (rows.length > LEAD_HISTORY_IMPORT_MAX_ROWS) {
    errors.push(`The file has ${rows.length} rows; the limit is ${LEAD_HISTORY_IMPORT_MAX_ROWS}.`);
  }
  return { rows, sheetRowNumbers, errors };
}
